<template>
  <div v-if="!selectedModel" class="projects-overview">
    <el-container class="screenHeight" v-loading="modelsLoading || isDeleting" element-loading-background="transparent">
      <el-container direction="vertical" class="fullHeight">
        <el-scrollbar>
          <el-main>
            <div class="media">
              <div class="media-content">
                <el-input v-model="searchText" :placeholder="$t('views.workInProgress.search')" />
              </div>
              <div class="media-right" v-if="this.featureFlagStore.isCreateProjectFromTemplateEnabled()">
                <el-dropdown max-height="calc(100vh - 10rem)">
                  <el-button type="primary">
                    {{ $t('views.workInProgress.create') }}
                  </el-button>
                  <template #dropdown>
                    <el-dropdown-menu>
                      <el-dropdown-item v-for="model in templateList" :key="model.modelId">
                        <el-card v-if="model.product" @click="createNewModel(model, $event)" style="max-width: 15rem; margin: 0.3rem 0 0.3rem 0;">
                          <template #header>
                            {{ model.product.name }}
                          </template>
                          <PreviewRendererImg
                            :width="'100%'"
                            :meshes="[
                              {
                                thumbnail: model.product.name,
                                thumbnailUrl: convertToUrl(model),
                              },
                            ]"
                          />
                        </el-card>
                      </el-dropdown-item>
                    </el-dropdown-menu>
                  </template>
                </el-dropdown>
              </div>
            </div>
            <el-space v-if="filterList.length > 0" wrap style="align-items: flex-start" class="cardSpace">
              <ModelCard
                v-for="model in filterList"
                :key="model.modelId"
                :title="model.product.name"
                :thumbnail-url="convertToUrl(model)"
                :show-delete="true"
                @delete="deleteModel(model.modelId)"
                @cardClick="displayModel(model, $event)"
              />
            </el-space>
            <el-empty v-else v-if="!modelsLoading" :description="$t('error.vuelidate.noProducts')" />
          </el-main>
        </el-scrollbar>
      </el-container>
    </el-container>
  </div>
</template>

<script lang="ts">
import { Vue, Component, Prop } from 'vue-facing-decorator';
import { ElMessage, ElMessageBox } from 'element-plus';

import * as modelService from '@/services/api/modelService';
import { Model2 } from '@/types/api/Model/Model';
import { FileTypeImage } from '@/types/enum/upload';
import PreviewRendererImg from '@/components/features/previewRenderer/PreviewRendererImg.vue';
import SideMenu from '@/components/workflow/SideMenu.vue';
import ToggleSidebar from '@/components/ui-elements/ToggleSidebar.vue';
import OrderWorkflow from '../order/OrderWorkflow.vue';
import { useRouter } from 'vue-router';
import * as ModelTypes from '@/types/enum/modelParameter';
import { useFeatureFlagStore } from '@/stores/featureFlagStore';
import ModelCard from '@/components/ui-elements/ModelCard.vue';
import { useUserStore } from '@/stores/userStore';

@Component({
  components: {
    SideMenu,
    ToggleSidebar,
    PreviewRendererImg,
    OrderWorkflow,
    ModelCard,
  },
})
/* eslint-disable @typescript-eslint/no-explicit-any */
@Component
export default class ProjectPage extends Vue {
  @Prop({ default: null })
  productTypeId!: number | null;

  userStore = useUserStore();

  modelList: Model2[] = [];
  templateList: Model2[] = [];
  FileTypeImage = FileTypeImage;
  showMenu = true;
  searchText = '';
  modelsLoading = false;
  isDeleting = false

  // New property to store the currently selected model
  selectedModel: Model2 | null = null;
  router = useRouter();
  featureFlagStore = useFeatureFlagStore();

  mounted(): void {
    if (!this.productTypeId) {
      this.router.back();
      return;
    }

    if(!this.featureFlagStore.isProjectListEnabled()){
      this.router.back();
      return 
    }
    const user = this.userStore.user;
    this.getModelsByUserId(user!.pk)
  }
  
  getModelsByUserId(userId: number){
    this.modelsLoading = true;
    // the productTypeId should be a filter option
    modelService.getModelsByUserId({
      userId:userId,
      // productTypeId:this.productTypeId,
      modelTypeParameter:ModelTypes.ModelTypeParameter.All,
      shouldRefetch:true
    }).then(async (result) => {
      if (result) {
        const modelList = result.filter((item) => item.basedOn);
        const templateList = result.filter((item) => !item.basedOn);
        this.modelList = modelList;
        this.templateList = templateList;
      } else {
        this.modelList = [];
        this.templateList = [];
      }
    })
    .catch((error) => {
      console.error("ProjectPage error", error)
    })
    .finally(()=> {
      this.modelsLoading = false;
    });
  }

  get filterList(): Model2[] {
    if (this.searchText.length > 0)
      return this.modelList.filter((item) => item.product && item.product.name?.toLowerCase().includes(this.searchText.toLowerCase()));
    return this.modelList;
  }

  convertToUrl(model: Model2): string | null {
    if (model.product && model.product.thumbnail) {
      return model.product.thumbnail.content;
    }
    return null;
  }

  deleteModel(modelId: number): void {
    ElMessageBox.confirm(this.$t('confirm.delete.message'), this.$t('confirm.delete.title'), {
      confirmButtonText: this.$t('confirm.delete.ok'),
      cancelButtonText: this.$t('confirm.delete.cancel'),
      type: 'warning',
    }).then(() => {
      this.isDeleting = true
      modelService.deleteModel(modelId).then(() => {
        ElMessage.success(this.$t('success.api.delete'));
        this.modelList = this.modelList.filter((item) => item.modelId !== modelId);
        this.isDeleting = false
      }).catch(()=>{
        ElMessage.error(this.$t('error.api.delete'));
        this.isDeleting = false
      })
    }).catch(()=>{
      console.log('[Delete Model] User canceled the action');
    })
  }

  // Instead of pushing a new route, we now set the selected model.
  displayModel(model: Model2, event: PointerEvent): void {
    const path = event.composedPath();
    if (!path.find((item) => (item as any).id === 'deleteModel')) {
      this.selectedModel = model;
      this.router.push(`/order-workflow?templateId=${model.basedOn.modelId}&orderId=${model.modelId}`);
    }
  }

  // For new models, we also simply set the selected model.
  createNewModel(model: Model2, event: PointerEvent): void {
    const path = event.composedPath();
    if (!path.find((item) => (item as any).id === 'deleteModel')) {
      this.selectedModel = model;
      this.router.push(`/order-workflow?templateId=${model.modelId}`);
    }
  }

  // Computed props for OrderWorkflow:
  // • For existing orders (i.e. models based on a template), we pass the template id and order id.
  // • For new orders, we pass the model id as the templateId and orderId stays null.
  get workflowTemplateId(): number {
    if (!this.selectedModel) return 0;
    return this.selectedModel.basedOn ? this.selectedModel.basedOn.modelId : this.selectedModel.modelId;
  }

  get workflowOrderId(): number | null {
    if (!this.selectedModel) return null;
    return this.selectedModel.basedOn ? this.selectedModel.modelId : null;
  }

  // Handler for when the OrderWorkflow component signals that the user wants to go back.
  handleWorkflowBack(): void {
    this.selectedModel = null;
  }
}
</script>

<style lang="scss" scoped>
/* Allow the component to fill the viewport and scroll if necessary */
:host {
  display: block;
  width: 100vw;
  height: 100vh;
  overflow: auto;
}

/* Outer container taking full height */
.screenHeight {
  width: 100%;
  height: 93vh;
  overflow-x: hidden;
}

/* Ensure the Element-UI container uses flex and full height */
.fullHeight {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.el-container {
  width: 100%;
  height: 93vh;
}

.media {
  padding-bottom: 1rem;
  display: flex;
  gap: 8px;
  align-items: center;
}

.el-main {
  padding: 1.5rem;
  overflow-y: auto;

  .cardSpace{
    gap: 8px !important;
  }

  > .media {
    display: flex;
    gap: 0.8rem;
    padding-bottom: 1rem;
  }
  .media {
    .media-content {
      width: 100%;
      font-size: 1rem;
    }
    .media-right {
      margin-left: 0;
      display: flex;
      align-items: center;
    }
  }
}

/* Optional spacing for cards */
.cardSpace {
  padding: 0.5rem;
  display: grid;
  grid-template-columns: repeat(auto-fit, 150px);
  justify-content: center;
  gap: 0 0.6rem !important;
}

.card-title {
  font-size: 1rem;
}

.products {
  transition: 250ms;
  width: 100%;
  cursor: pointer;

  h2 {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin: 0;
  }
}

.el-card:hover {
  scale: 1.04;
}

.el-card {
  transition: 250ms;
  width: 100%;
  cursor: pointer;
}

:deep(.el-card__header) {
  padding: 0.5rem 1rem 0.5rem 1rem;
}

:deep(.el-card__body) {
  display: grid;
  justify-content: center;
}

/* When OrderWorkflow is active, allow scrolling if content is tall */
.orderWorkflowWrapper {
  width: 100%;
  height: 100%;
  overflow-y: auto;
}
</style>
